import { useEffect, useState } from "react";
import DragAndDropContainer from "../../../../ui library/components/file-upload/DragAndDropContainer";
import AutoCompleteDropdown from "../../../../ui library/components/dropdowns/AutoCompleteDropdown";
import BasicDropdown from "../../../../ui library/components/dropdowns/BasicDropdown";
import BasicButton from "../../../../ui library/components/buttons/BasicButton";
import useService from "../../../../../hooks/useService";
import { useParams } from "react-router-dom";
import DocumentUploadHeader from "./DocumentUploadHeader";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import ResponseModal from "../../../../global/ResponseModal";
import LoadingIndicator from "../../../../global/LoadingIndicator";
import { useDocumentListStore } from "../../ViewDocuments/hooks/useDocumentListStore";

const DocumentUpload = ({
  selectedProviderId,
  isLaunchedFromAppointmentsTab,
  setIsLaunchedFromAppointmentsTab,
}) => {
  const documentRepository = useService("repositories/document-repository");
  const documentValidateRepository = useService("repositories/document-validate-repository");
  const providerClientStatusRepository = useService("repositories/provider-client-status-repository");
  const { id } = useParams();

  const [fileReq, setFileReq] = useState("");
  const [documentsOptions, setDocumentsOptions] = useState(null);
  const [documentValidations, setDocValidations] = useState(null);
  const [providers, setProviders] = useState(null);
  const [isDocSourceRequired, setIsDocSourceRequired] = useState(false);
  const [isRelatedProviderRequired, setIsRelatedProviderRequired] =
    useState(false);
  const [isReferralFromRequired, setIsReferralFromRequired] = useState(false);
  const [isReferralToRequired, setIsReferralToRequired] = useState(false);
  const [maxDocSize, setMaxDocSize] = useState("Max Size: 100MB");
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [selectedDocSource, setSelectedDocSource] = useState(null);
  const [selectedRelatedProvider, setSelectedRelatedProvider] = useState(null);
  const [selectedReferralFrom, setSelectedReferralFrom] = useState(null);
  const [selectedReferralTo, setSelectedReferralTo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [errorArray, setErrorArray] = useState([]);
  const [errorStatus, setErrorStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initDocType, setInitDocType] = useState(null);
  const [initDocSource, setInitDocSource] = useState("");
  const [initRelatedProvider, setInitRelatedProvider] = useState("");
  const setIsUploadModalOpen = useDocumentListStore((state) => state.setIsUploadModalOpen);


  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const options = await documentRepository.getOptions();
        const documentValidation = await documentValidateRepository.get();
        const providersClient = await providerClientStatusRepository.query({
          case: id,
        });
        const providers = providersClient.map((client) => client?.provider);

        const mappedDocTypes =
          options?.docTypeOptions?.map((opt) => ({
            id: opt.value,
            label: opt.display_name,
          })) || [];

        const mappedDocSources =
          options?.sourceOptions?.map((opt) => ({
            id: opt.value,
            label: opt.display_name,
          })) || [];

        const mappedProviders =
          providers.map((provider) => ({
            id: provider.id,
            label: provider.name,
          })) || [];

        setDocumentsOptions({
          docTypeOptions: mappedDocTypes,
          sourceOptions: mappedDocSources,
        });
        setDocValidations(documentValidation);
        setMaxDocSize(documentValidation?.maxFileSize);
        setProviders(mappedProviders);

        if(isLaunchedFromAppointmentsTab) {
          const selectedDocType = mappedDocTypes.filter(docType => docType.id === 23)[0];
          const selectedDocSource = mappedDocSources.filter(docSource => docSource.id === 3)[0];
          const selectedRelatedProvider = mappedProviders.filter(relatedProvider => relatedProvider.id === selectedProviderId)[0];
          setInitDocType(selectedDocType);
          setInitDocSource(selectedDocSource);
          setInitRelatedProvider(selectedRelatedProvider);
          setSelectedDocSource(selectedDocSource);
          setSelectedRelatedProvider(selectedRelatedProvider);
          setSelectedDocType(selectedDocType);
          docTypeValidate(documentValidation, selectedDocType);
        }
      } catch (error) {
         handleNeedsTypeFail(error);
      }
    };

    fetchOptions();
  }, [documentRepository, documentValidateRepository, id]);

  const onClearDocType = () => {
    setFileReq("");
    setIsRelatedProviderRequired(false);
    setIsDocSourceRequired(false);
    setIsReferralFromRequired(false);
    setIsReferralToRequired(false);
  };

  const resetAll = () => {
    setFileReq("");
    setIsRelatedProviderRequired(false);
    setIsDocSourceRequired(false);
    setIsReferralFromRequired(false);
    setIsReferralToRequired(false);
    setSelectedDocType(initDocType);
    setSelectedDocSource(initDocSource);
    setSelectedRelatedProvider(initRelatedProvider);
    setSelectedReferralFrom("");
    setSelectedReferralTo("");
    setSelectedFile("");

    if(isLaunchedFromAppointmentsTab) {
      docTypeValidate(documentValidations, initDocType);
    }
  };

  const docTypeValidate = (docValidationList, value) => {
    setFileReq(docValidationList?.validExtensions[value?.id].join(", "));
    setIsDocSourceRequired(
      docValidationList?.required?.source?.includes(value?.id)
    );
    if (!isLaunchedFromAppointmentsTab) {
      setIsRelatedProviderRequired(
        docValidationList?.required?.provider?.includes(value?.id)
      );
    } else {
      setIsRelatedProviderRequired(true);
    }
    setIsReferralFromRequired(
      docValidationList?.required?.referral_from?.includes(value?.id)
    );
    setIsReferralToRequired(
      docValidationList?.required?.referral_to?.includes(value?.id)
    );
  };

  const onChangeDocType = (e, value) => {
    setSelectedDocType(value);
    if (!value) {
      onClearDocType();
      return;
    }
    docTypeValidate(documentValidations, value);
  };

  const onChangeDocSource = (e, value) => {
    setSelectedDocSource(value);
  };

  const onChangeRelatedProvider = (e, value) => {
    setSelectedRelatedProvider(value);
  };

  const onChangeReferralFrom = (e, value) => {
    setSelectedReferralFrom(value);
  };

  const onChangeReferralTo = (e, value) => {
    setSelectedReferralTo(value);
  };

  const onChangeFile = (e, value) => {
    setSelectedFile(value);
  };

  const onUploadBtnClick = async () => {
    setLoading(true);
    const postData = {
      case: id,
      document_type: selectedDocType?.id,
      file: selectedFile,
    };

    if(isDocSourceRequired || selectedDocSource?.id) {
      postData.source = selectedDocSource?.id;
    }

    if (isRelatedProviderRequired && selectedRelatedProvider?.id) {
      postData.provider = selectedRelatedProvider.id;
    }

    if (
      isLaunchedFromAppointmentsTab ||
      (isReferralFromRequired && selectedReferralFrom?.id)
    ) {
      postData.referral_from = selectedReferralFrom.id;
    }

    if (isReferralToRequired && selectedReferralTo?.id) {
      postData.referral_to = selectedReferralTo.id;
    }

    await documentRepository
      .upload(postData)
      .then((res) => {
        setUploadSuccess(true);
        return res;
      })
      .catch((error) => {
        handleNeedsTypeFail(error);
      });
    setLoading(false);
  };

  const handleUploadSuccess = () => {
    setUploadSuccess(false);
    resetAll();
    setIsUploadModalOpen(false);
  };
  const handleNeedsTypeFail = (error) => {
      setUploadFailed(true);
      let errArr = ApiRequestErrorHandler(error?.response?.data || error);
      setErrorArray(errArr);
  };

  return (
    <div className="h-full">
      <DocumentUploadHeader maxDocSize={maxDocSize} fileReq={fileReq} />

      <div className="py-2">
        <div className="w-full my-8 mx-auto" data-testid="container-docTypes">
          <AutoCompleteDropdown
            initValue={selectedDocType}
            label="Document Types"
            data={documentsOptions?.docTypeOptions || []}
            onValueChange={(e, value) => {
              onChangeDocType(e, value);
            }}
          />
        </div>

        <div className="w-full my-8 mx-auto" data-testid="container-docSource">
          <BasicDropdown
            initValue={selectedDocSource}
            label={"Document Source" + (isDocSourceRequired ? "*" : "")}
            data={documentsOptions?.sourceOptions || []}
            onValueChange={onChangeDocSource}
            addNoSelectionOption={!isDocSourceRequired}
          />
        </div>

        {isRelatedProviderRequired && (
          <div
            className="w-full my-8 mx-auto"
            data-testid="container-relatedProvider"
          >
            <BasicDropdown
              initValue={selectedRelatedProvider}
              label={"Related Provider*"}
              data={providers || []}
              onValueChange={onChangeRelatedProvider}
              addNoSelectionOption={false}
              disabled={isLaunchedFromAppointmentsTab}
            />
          </div>
        )}

        {(isReferralFromRequired || isReferralToRequired) && (
          <div className="mx-4 mt-1 mb-10 p-4 text-black rounded-sm bg-yellow-100 text-sm">
            <strong>NOTE: </strong>Only providers that are already assigned to
            the client's case can be chosen below. If the provider needed is not
            shown below, please add them to the case before uploading the
            referral file.
          </div>
        )}

        {isReferralFromRequired && (
          <div
            className="w-full my-8 mx-auto"
            data-testid="container-referralFrom"
          >
            <BasicDropdown
              initValue={selectedReferralFrom}
              label={"Referral From*"}
              data={providers || []}
              onValueChange={(e, value) => {
                onChangeReferralFrom(e, value);
              }}
              addNoSelectionOption={false}
            />
          </div>
        )}

        {isReferralToRequired && (
          <div
            className="w-full my-8 mx-auto"
            data-testid="container-referralTo"
          >
            <BasicDropdown
              initValue={selectedReferralTo}
              label={"Referral To*"}
              data={providers || []}
              onValueChange={(e, value) => {
                onChangeReferralTo(e, value);
              }}
              addNoSelectionOption={false}
            />
          </div>
        )}

        <div
          className="w-full my-8 mx-auto"
          data-testid="container-dragAndDropBox"
        >
          <DragAndDropContainer
            initValue={selectedFile}
            onValueChange={onChangeFile}
          />
        </div>

        <div
          className="w-full my-8 mx-auto text-center flex flex-row justify-evenly gap-4"
          data-testid="container-uploadButton"
        >
          <BasicButton
            text="Upload Document"
            className="w-1/2 bg-blue-600 px-4 py-2 items-center self-center text-lg hover:bg-blue-700"
            onClick={onUploadBtnClick}
          />
          <BasicButton
            variant="outlined"
            text="Clear"
            className="w-1/2 px-4 py-2 text-blue-600 items-center self-center text-lg hover:bg-blue-100"
            onClick={resetAll}
          />
        </div>
      </div>
      <LoadingIndicator isLoading={loading} />
      <ResponseModal
        title="Update Success"
        description="The image/document was successfully updated"
        openBool={uploadSuccess}
        setOpenBool={setUploadSuccess}
        handleCloseFunc={handleUploadSuccess}
      />
      <ResponseModal
        title="Update failed"
        isError={true}
        openBool={uploadFailed}
        setOpenBool={setUploadFailed}
        errorMessage={errorArray}
      />
    </div>
  );
};

export default DocumentUpload;
